'use client';

import { useState } from 'react';
import { Button } from '@write4me/ui/components/button';
import { useSignInWithGoogle } from '@/lib/query/hooks/use-auth-mutations';

interface GoogleSignInButtonProps {
  redirectTo?: string;
  disabled?: boolean;
  /**
   * Role chosen on the signup page. When set, written to a short-lived
   * `pending_role` cookie just before redirecting to Google so the auth
   * callback can apply it to `user_metadata.role` for first-time Google
   * sign-ups (onboarding then renders without a role selector).
   */
  pendingRole?: 'employer' | 'writer';
}

export function GoogleSignInButton({
  redirectTo,
  disabled,
  pendingRole,
}: GoogleSignInButtonProps) {
  const signInGoogleMutation = useSignInWithGoogle();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGoogleSignIn() {
    setLoading(true);
    setError(null);

    try {
      if (pendingRole === 'employer' || pendingRole === 'writer') {
        // 10-minute TTL — long enough for the OAuth round-trip; short enough
        // that a stale role doesn't leak into a later sign-in attempt.
        document.cookie = `pending_role=${pendingRole}; path=/; max-age=600; samesite=lax`;
      }

      const result = await signInGoogleMutation.mutateAsync({
        redirectTo,
      });

      // Redirect to Google OAuth
      if (result?.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in with Google');
      setLoading(false);
    }
  }

  return (
    <div>
      <Button
        type="button"
        variant="secondary"
        size="lg"
        loading={loading}
        disabled={disabled}
        onClick={handleGoogleSignIn}
        className="w-full"
      >
        <GoogleIcon />
        Continue with Google
      </Button>
      {error && (
        <p className="text-xs font-ui text-error mt-2 text-center" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
